/**
 * The headless ranking model (DESIGN.md §3.3).
 *
 * This module is pure: no React, no DOM, no dnd-kit.
 *
 * It is three functions rather than a state machine because the list is
 * complete from the first frame — every player, ranks 1..n, alphabetical. With
 * nothing partial there is no held card, no armed slot and no placement mode,
 * so there is nothing for the input methods to leak across and no way to
 * duplicate or lose a player: `ranking` is a permutation of `order` by
 * construction, and `moveTo` is the only thing that touches it.
 */
import type { PlayerId, Ranking } from '@idgame/protocol';

export interface RankingModel {
  /** The seed order — the roster, alphabetised. Also what a newcomer joins. */
  readonly order: readonly PlayerId[];
  /** The current ranking, first place first. Always the whole roster. */
  readonly ranking: readonly PlayerId[];
}

/* -------------------------------------------------------------------------- */
/* Construction                                                                */
/* -------------------------------------------------------------------------- */

/**
 * `saved` is a restored draft (see `rankingDraft.ts`) and is therefore
 * untrusted: it can name players who have left, repeat one, or be short. It is
 * used as a *preference* — anyone it does not account for keeps their seed
 * position relative to the rest — so a stale draft degrades to a partial
 * restore rather than to a broken board.
 */
export function createRankingModel(
  order: readonly PlayerId[],
  saved?: readonly PlayerId[],
): RankingModel {
  return { order, ranking: seat(order, saved) };
}

/**
 * Re-seat against a new roster (a late join, or a reconnect snapshot). Work
 * already done survives: everyone still present keeps their relative order and
 * newcomers land at the bottom rather than alphabetically, because dropping
 * someone into the middle of a ranking the player is halfway through arranging
 * would silently move an answer they had already decided.
 */
export function reconcileRankingModel(
  model: RankingModel,
  order: readonly PlayerId[],
): RankingModel {
  if (sameOrder(model.order, order)) return model;
  return { order, ranking: seat(order, model.ranking) };
}

/** `order`, resequenced by whatever of `preferred` still applies. */
function seat(
  order: readonly PlayerId[],
  preferred: readonly PlayerId[] | undefined,
): readonly PlayerId[] {
  if (!preferred) return [...order];
  const roster = new Set(order);
  const seen = new Set<PlayerId>();
  const out: PlayerId[] = [];
  for (const id of preferred) {
    if (!roster.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  for (const id of order) {
    if (!seen.has(id)) out.push(id);
  }
  return out;
}

function sameOrder(a: readonly PlayerId[], b: readonly PlayerId[]): boolean {
  return a.length === b.length && a.every((id, i) => b[i] === id);
}

/* -------------------------------------------------------------------------- */
/* Derived                                                                     */
/* -------------------------------------------------------------------------- */

/** Zero-based position, or `null` if the player is not on the board. */
export function indexOf(model: RankingModel, id: PlayerId): number | null {
  const index = model.ranking.indexOf(id);
  return index === -1 ? null : index;
}

/** Always submittable: the board is never incomplete. */
export function toRanking(model: RankingModel): Ranking {
  return [...model.ranking];
}

/* -------------------------------------------------------------------------- */
/* Transitions                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Move `id` to `index`, closing the gap behind it. The single write path: a
 * drop and a keyboard move are the same call, which is what keeps the invisible
 * accessible path (DESIGN §3.3) honest rather than a second, drifting mode.
 */
export function moveTo(model: RankingModel, id: PlayerId, index: number): RankingModel {
  const from = indexOf(model, id);
  if (from === null) return model;
  const to = clamp(index, 0, model.ranking.length - 1);
  if (from === to) return model;
  const ranking = [...model.ranking];
  ranking.splice(from, 1);
  ranking.splice(to, 0, id);
  return { order: model.order, ranking };
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
